import { useMemo, useState } from "react"
import { useBreakpoints } from "@sembark-travel/ui-base"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import useSWR from "swr"
import {
	cancellationPoliciesXHR,
	type TCancellationPolicy,
} from "../CancellationPolicy"
import { paymentPoliciesXHR, type TPaymentPolicy } from "../PaymentPolicy"
import type { IHotel } from "../Hotels"
import type { TOtaMealPlan } from "../HotelOtaMealPlans"
import type { TOtaRoomType } from "../HotelOtaRoomTypes"
import type { TPolicyVersionSummary } from "../shared/policies"
import { IUser } from "../Users"
import type { ITripSource } from "../TripSources"
import type { INewRateSheetFormSchema } from "./NewItem"
import {
	headToFormData,
	policyHeadId,
	policyHeadName,
	type TAnyPolicy,
	type TPayload,
	type TPolicyHeads,
	type TPolicyKind,
} from "./utils"
import { TCurrencyCode, TFormattedMoney } from "@sembark-travel/money"

export type TDay =
	| "Sunday"
	| "Monday"
	| "Tuesday"
	| "Wednesday"
	| "Thursday"
	| "Friday"
	| "Saturday"

export type TRateSheetSeasonInterval = {
	id: number
	from: string
	to: string
	weekdays_bitmap: number
	weekdays_bitmap_str: string
}

export type TRate = {
	id: number
	ota_meal_plan: TOtaMealPlan
	ota_room_type: TOtaRoomType
	occupancy: string
	currency: TCurrencyCode
	rate_before_tax: string
	rate_before_tax_money: TFormattedMoney
	// Null for a manual rate and for a block that does not round
	rate_before_tax_unrounded: string | null
	rate_before_tax_unrounded_money: TFormattedMoney | null
	is_manual: boolean
}

export type TMealRate = {
	id: number
	// Only breakfast, lunch or dinner. null for a rate priced against a custom
	// meal plan, which is identified by `ota_meal_plan` instead
	meal_type: string | null
	ota_meal_plan: TOtaMealPlan | null
	currency: TCurrencyCode
	per_adult_rate_before_tax: string
	per_adult_rate_before_tax_money: TFormattedMoney
	per_child_rate_before_tax: string
	per_child_rate_before_tax_money: TFormattedMoney
}

export type TRateSheetMealPlan = {
	id: number
	ota_meal_plan: TOtaMealPlan
	// Empty when the plan includes all room types
	ota_room_types: TOtaRoomType[]
	includes_all_room_types: boolean
	meal_category: string | null
	is_base: boolean
}

// The room types a block prices, with the occupancies it priced them at.
export type TRateSheetRoom = {
	id: number
	ota_room_type: TOtaRoomType
	base_adult_occupancy: number
	base_child_occupancy: number
	max_occupancy: number
	max_adult_occupancy: number
	max_child_occupancy: number
}

// A pax type priced by a block — one per column of its rate table. BASE has no
// count of its own, only child pax types cover an age band, and only a derived
// column names what it derives from; the derivation factors arrive as decimal
// strings ("0.5", "100").
export type TRateSheetPaxType = {
	id: number
	occupancy: string
	pax_type: string
	pax_count: number | null
	start_age: number | null
	end_age: number | null
	derived_from_occupancy: string | null
	derivation_multiplier: string | null
	derivation_adder: string | null
}

export type TRoundingPointSchema = { id: number | null; name: string }

export type TPricingType = "RBP" | "OBP"

// A policy version as a rate sheet embeds it: named, and carrying its head's
// latest version so a stale pin shows without the policy listings.
export type TRateSheetPolicy = TPolicyVersionSummary & {
	head_name: string
	latest_version: number
}

// Everything priced for one hotel in one season. The block describes its own
// grid — `rooms` are its rows and `pax_types` its columns — which is what lets a
// saved sheet render without the hotel's OTA configuration.
export type TRateSheetRateBlock = {
	id: number
	hotel: IHotel
	meal_plans: TRateSheetMealPlan[]
	meal_rates: TMealRate[]
	rooms: TRateSheetRoom[]
	pax_types: TRateSheetPaxType[]
	rates: TRate[]
	// Null when the block does not round
	rounding_point: number | null
	pricing_type: TPricingType
	child_meal_rate_same_as_adult: boolean
	is_lower_occupancy_rate_configured: boolean
	// The policy versions the block sells under — its hotel's, unless the
	// matching `_is_manual` flag says the block overrode them
	cancellation_policy: TRateSheetPolicy
	cancellation_policy_is_manual: boolean
	payment_policy: TRateSheetPolicy
	payment_policy_is_manual: boolean
}

export type TRateSheetSeason = {
	id: number
	name: string
	intervals: TRateSheetSeasonInterval[]
	blocks: TRateSheetRateBlock[]
}

// The sheet's hotels, with the policy versions every block of that hotel sells
// under.
export type TRateSheetHotel = {
	id: number
	cancellation_policy: TRateSheetPolicy
	payment_policy: TRateSheetPolicy
	hotel: IHotel
}

// `hotels_count` and `seasons_count` are null on the versions listing, which
// does not count them.
export type TRateSheet = {
	id: number
	version: number
	rate_sheet_head_id: number
	hotels: TRateSheetHotel[]
	hotels_count: number | null
	seasons: TRateSheetSeason[]
	seasons_count: number | null
	created_at: string
	updated_at: string
	created_by: IUser
}

// Very Important, need to handle verify this with backend data as we maintain head not detail now.
export type TRateSheetVersion = Omit<TRateSheet, "hotels" | "seasons">

// A sheet as the detail endpoint returns it: the head, which carries the name,
// with its latest version inline.
export type TRateSheetHead = {
	id: number
	name: string
	// Whether the sheet is for every DMC, without having been shared with each
	applies_to_all_trip_sources: boolean
	latest_rate_sheet: TRateSheet
	created_at: string
	created_by: IUser
	updated_at: string
	// Null until the sheet is edited
	updated_by: IUser | null
	deleted_at: string | null
}

// A row of the listing — the same head as the detail endpoint returns.
export type TRateSheetDetail = TRateSheetHead & {
	shared_with_trip_sources_count?: number
	shared_with_trip_sources_behind_count?: number
}

// A share record: a rate sheet head having been shared with a trip source. The
// trip source itself is nested under `trip_source`, alongside the version the
// trip source is on (`version` / `versions_behind`).
export type TRateSheetShare = {
	id: number
	trip_source: Pick<ITripSource, "id" | "name" | "short_name">
	rate_sheet_id: number
	version: number
	latest_version: number
	versions_behind: number
}

// The flag is required on every share request. The trip sources are only sent
// when naming DMCs. The upgrade key is sent only when a sheet that was applied
// to all DMCs is switched back to named ones, so the DMCs it was shared with
// before are moved to the latest version.
export type TShareTripSourcesPayload =
	| { applies_to_all_trip_sources: 1 }
	| {
			applies_to_all_trip_sources: 0
			trip_sources: number[]
			upgrade_existing_trip_sources_to_latest_version?: 1
	  }

export function rateSheetsXHR(xhr: XHRInstance) {
	return {
		async get(params?: Record<string, unknown>) {
			return await xhr
				.get<IListResponse<TRateSheetDetail>>("/rate-sheet-heads", { params })
				.then((resp) => resp.data)
		},
		async getById(id: number): Promise<TRateSheetHead> {
			return xhr
				.get<{ data: TRateSheetHead }>(`/rate-sheet-heads/${id}`)
				.then((resp) => resp.data.data)
		},
		async create(payload: TPayload): Promise<TRateSheetHead> {
			return xhr
				.post<{ data: TRateSheetHead }>("/rate-sheet-heads", payload)
				.then((resp) => resp.data.data)
		},
		async update(id: number, payload: TPayload): Promise<TRateSheetHead> {
			return xhr
				.put<{ data: TRateSheetHead }>(`/rate-sheet-heads/${id}`, payload)
				.then((resp) => resp.data.data)
		},
		async archive(id: number) {
			return xhr.delete(`/rate-sheet-heads/${id}`).then((resp) => resp.data)
		},
		async restore(id: number) {
			return xhr
				.patch(`/rate-sheet-heads/${id}/restore`)
				.then((resp) => resp.data)
		},
		async getVersions(id: number, params?: Record<string, unknown>) {
			return await xhr
				.get<IListResponse<TRateSheetVersion>>(
					`/rate-sheet-heads/${id}/rate-sheets`,
					{
						params,
					}
				)
				.then((resp) => resp.data)
		},
		async getVersionById(id: number): Promise<TRateSheet> {
			return xhr
				.get<{ data: TRateSheet }>(`/rate-sheets/${id}`)
				.then((resp) => resp.data.data)
		},
		async getTripSourcesForRateSheet(
			headId: number,
			params?: { q?: string; limit?: number; page?: number }
		): Promise<IListResponse<TRateSheetShare>> {
			return xhr
				.get<
					IListResponse<TRateSheetShare>
				>(`/rate-sheet-heads/${headId}/trip-sources`, { params })
				.then((resp) => resp.data)
		},
		async shareTripSources(
			headId: number,
			payload: TShareTripSourcesPayload
		): Promise<IListResponse<TRateSheetShare>> {
			return xhr
				.post<
					IListResponse<TRateSheetShare>
				>(`/rate-sheet-heads/${headId}/trip-sources`, payload)
				.then((resp) => resp.data)
		},
		async revokeTripSourceAccess(
			headId: number,
			tripSourceId: number
		): Promise<{ message: string }> {
			return xhr
				.delete<{
					data: { message: string }
				}>(`/rate-sheet-heads/${headId}/trip-sources/${tripSourceId}`)
				.then((resp) => resp.data.data)
		},
		async updateTripSourcesToLatest(
			headId: number
		): Promise<IListResponse<TRateSheetShare>> {
			return xhr
				.patch<
					IListResponse<TRateSheetShare>
				>(`/rate-sheet-heads/${headId}/trip-sources/update-to-latest`)
				.then((resp) => resp.data)
		},
	}
}

export type TSharedRateSheetHead = Omit<
	TRateSheetHead,
	"latest_rate_sheet" | "name" | "applies_to_all_trip_sources"
> & {
	tenant_name: string
	rate_sheet: TRateSheet
}

export type TSharedRateSheet = TRateSheet & {
	tenant_name: string
}

// Calls made through the shared (token) link. The token itself rides on the
// xhr instance the shared layout provides, so it is not passed in here.
export function sharedRateSheetsXHR(xhr: XHRInstance) {
	return {
		async get(params?: {
			page?: number
			limit?: number
		}): Promise<IListResponse<TSharedRateSheetHead>> {
			return xhr
				.get<
					IListResponse<TSharedRateSheetHead>
				>("/guest/rate-sheet-heads", { params })
				.then((resp) => resp.data)
		},
		// Only the versions up to the one that was shared
		async getVersions(headId: number, params?: Record<string, unknown>) {
			return await xhr
				.get<
					IListResponse<TRateSheetVersion>
				>(`/guest/rate-sheet-heads/${headId}/rate-sheets`, { params })
				.then((resp) => resp.data)
		},
		async getVersionById(id: number): Promise<TSharedRateSheet> {
			return xhr
				.get<{ data: TSharedRateSheet }>(`/guest/rate-sheets/${id}`)
				.then((resp) => resp.data.data)
		},
	}
}

// The calls the version history dialog makes, which both the signed in and the
// shared (token) api provide
export type TRateSheetVersionsXHR = (
	xhr: XHRInstance
) => Pick<ReturnType<typeof rateSheetsXHR>, "getVersions" | "getVersionById">

// React-side helpers shared by the wizard (`NewItem`), the detail page (`Item`)
// and the edit page. `utils.ts` stays free of React so its data helpers can be
// tested as plain functions — anything holding state or fetching lives here.

export function useIsMobile() {
	const { xs, sm } = useBreakpoints()
	return xs || sm
}

// One option per policy head, keyed by the head rather than by its latest
// version: a sheet pinned to an older version has to map back to the policy it
// belongs to, and picking an option always means "the latest version of this
// policy". Every screen that needs the listings shares these SWR keys, so the
// wizard fetches each of them once.
export function useSheetPolicyOptions() {
	const xhr = useXHR()
	const { data: cancellationPolicies } = useSWR(
		"/cancellation-policy-heads",
		() => cancellationPoliciesXHR(xhr).get()
	)
	const { data: paymentPolicies } = useSWR("/payment-policy-heads", () =>
		paymentPoliciesXHR(xhr).get()
	)

	return useMemo(() => {
		const toOptions = (
			heads: Array<{ id: number; name: string }> | undefined
		) => heads?.map((head) => ({ id: Number(head.id), name: head.name })) || []
		return {
			policyHeads: {
				cancellation: cancellationPolicies?.data,
				payment: paymentPolicies?.data,
			} satisfies TPolicyHeads,
			cancellationOptions: toOptions(cancellationPolicies?.data),
			paymentOptions: toOptions(paymentPolicies?.data),
		}
	}, [cancellationPolicies, paymentPolicies])
}

// The rules of a policy version a sheet is pinned to. The listings only carry
// each head's latest version, so anything older has to be read by version id
// before its rules can be rendered. Returns the policy itself when it already
// holds its rules, which is the common case.
export function usePinnedPolicyVersion<T extends TAnyPolicy>(
	kind: TPolicyKind,
	policy: T | null | undefined
): T | null {
	const xhr = useXHR()
	const hasRules = Boolean(
		(policy as TCancellationPolicy | undefined)?.rules?.length ||
		(policy as TPaymentPolicy | undefined)?.rule_sets?.length
	)
	const versionId = policy && !hasRules ? Number(policy.id) : null
	const { data } = useSWR<TAnyPolicy>(
		versionId
			? kind === "cancellation"
				? `/cancellation-policies/${versionId}`
				: `/payment-policies/${versionId}`
			: null,
		() =>
			kind === "cancellation"
				? cancellationPoliciesXHR(xhr).getVersionById(Number(versionId))
				: paymentPoliciesXHR(xhr).getVersionById(Number(versionId))
	)

	if (!policy) return null
	if (hasRules) return policy
	if (!data) return policy
	// The fetched version carries the rules but not the head's name, which only
	// the listing knows — so the name the caller already resolved wins over
	// whatever the version does or does not carry.
	const headId = policyHeadId(policy) ?? policyHeadId(data)
	const headName = policyHeadName(policy) ?? policyHeadName(data)
	return {
		...data,
		head_id: headId ?? undefined,
		head_name: headName,
	} as unknown as T
}

// The sheet as the detail endpoint returns it. The detail page and the edit
// wizard share this SWR key, so opening Edit from the detail page costs no
// extra request.
export function useRateSheetHead(id: number | string | null | undefined) {
	const xhr = useXHR()
	return useSWR(id ? `/rate-sheet-heads/${id}` : null, () =>
		rateSheetsXHR(xhr).getById(Number(id))
	)
}

// The version of a sheet that was shared through a token link. Must be used
// below the shared layout, whose xhr instance carries the token.
export function useSharedRateSheet(id: number) {
	const xhr = useXHR()
	return useSWR(`/guest/rate-sheets/${id}`, () =>
		sharedRateSheetsXHR(xhr).getVersionById(id)
	)
}

// Loads a saved sheet into the edit wizard's form values. The sheet names its
// policies itself; the policy listings are fetched alongside for what only they
// know — each head's latest version with its rules, which the wizard's selects,
// "Update to Latest" and stale policy errors are built on.
export function useRateSheetFormData(id: number | string | null | undefined) {
	const { data: head, isLoading, isValidating, error } = useRateSheetHead(id)
	const { policyHeads } = useSheetPolicyOptions()

	const sheet = useMemo(
		() => (head ? headToFormData(head, { policyHeads }) : null),
		[head, policyHeads]
	)

	return { head, sheet, policyHeads, isLoading, isValidating, error }
}

// A sheet view measures warnings and changes across every hotel in the sheet,
// not just the one on screen, and that needs each hotel's full OTA room types
// and meal plans. Neither record step 1 holds is enough: the hotel picker's list
// endpoint carries none of it, and a sheet loaded for editing carries its room
// types but not the rest of the configuration the grid is built from. So every
// hotel is fetched, once for the whole sheet rather than per visited tab, and
// the header counts are complete the moment the step opens.
//
// Only fetched records are returned — a hotel missing from the map has not
// loaded yet, which the summary reads as "nothing to measure warnings against".
export function useSheetHotelDetails(hotels: IHotel[]): Map<number, IHotel> {
	const xhr = useXHR()

	const hotelIds = useMemo(
		() => hotels.map((hotel) => Number(hotel.id)).sort((a, b) => a - b),
		[hotels]
	)

	const { data: fetched } = useSWR(
		hotelIds.length ? `/rate-sheet-hotel-details/${hotelIds.join(",")}` : null,
		() =>
			Promise.all(
				hotelIds.map((hotelId) =>
					xhr
						.get<{ data: IHotel }>(`/hotels/${hotelId}`)
						.then((resp) => resp.data.data)
				)
			)
	)

	return useMemo(() => {
		const byId = new Map<number, IHotel>()
		fetched?.forEach((hotel) => byId.set(Number(hotel.id), hotel))
		return byId
	}, [fetched])
}

// Which hotel and season a sheet view is showing. Selections are held loosely
// and validated on the way out rather than corrected in an effect: the hotels
// and seasons can be replaced wholesale (the duplicate flow, a version switch)
// and a stale index would render an empty panel for a frame.
export function useSheetSelection(
	hotels: NonNullable<INewRateSheetFormSchema["hotels"]>,
	seasons: NonNullable<INewRateSheetFormSchema["seasons"]>
) {
	const [selectedHotelId, setSelectedHotelId] = useState<number | null>(null)
	const [selectedSeasonIdx, setSelectedSeasonIdx] = useState(0)

	const activeHotelId =
		selectedHotelId !== null &&
		hotels.some((hotel) => Number(hotel.id) === selectedHotelId)
			? selectedHotelId
			: hotels.length
				? Number(hotels[0].id)
				: 0
	const activeSeasonIdx =
		selectedSeasonIdx < seasons.length ? selectedSeasonIdx : 0

	return {
		activeHotelId,
		activeSeasonIdx,
		setSelectedHotelId,
		setSelectedSeasonIdx,
	}
}
